export interface LocalSessionAccount {
  userId: string;
  username: string;
  accessToken: string;
  refreshToken: string;
  identitySkRaw?: string;
  lastUsedAt: string;
}

const ACCOUNTS_KEY = 'atten:accounts';

function readAccounts(): LocalSessionAccount[] {
  const raw = localStorage.getItem(ACCOUNTS_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter((item): item is LocalSessionAccount => {
      if (typeof item !== 'object' || !item) {
        return false;
      }
      const value = item as Partial<LocalSessionAccount>;
      return (
        typeof value.userId === 'string' &&
        typeof value.username === 'string' &&
        typeof value.accessToken === 'string' &&
        typeof value.refreshToken === 'string' &&
        typeof value.lastUsedAt === 'string'
      );
    });
  } catch {
    return [];
  }
}

function writeAccounts(accounts: LocalSessionAccount[]): void {
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

export function saveOrUpdateAccount(account: Omit<LocalSessionAccount, 'lastUsedAt'>): LocalSessionAccount {
  const now = new Date().toISOString();
  const accounts = readAccounts();
  const next: LocalSessionAccount = { ...account, lastUsedAt: now };
  const index = accounts.findIndex((item) => item.userId === account.userId);

  if (index >= 0) {
    accounts[index] = next;
  } else {
    accounts.push(next);
  }

  writeAccounts(accounts);
  return next;
}

export function setAccountIdentityKey(userId: string, identitySkRaw: string): void {
  const accounts = readAccounts();
  const index = accounts.findIndex((item) => item.userId === userId);
  if (index < 0) {
    return;
  }

  const existing = accounts[index];
  if (!existing) {
    return;
  }

  accounts[index] = {
    ...existing,
    identitySkRaw,
    lastUsedAt: new Date().toISOString()
  };
  writeAccounts(accounts);
}

export function applyAccountToSession(account: Pick<LocalSessionAccount, 'userId' | 'accessToken' | 'refreshToken' | 'identitySkRaw'>): void {
  localStorage.setItem('userId', account.userId);
  localStorage.setItem('accessToken', account.accessToken);
  localStorage.setItem('refreshToken', account.refreshToken);
  if (account.identitySkRaw) {
    localStorage.setItem('identitySkRaw', account.identitySkRaw);
  }
}

export function getMostRecentlyUsedAccount(): LocalSessionAccount | null {
  const accounts = readAccounts().sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt));
  return accounts[0] ?? null;
}

export function ensureSessionFromStoredAccount(): LocalSessionAccount | null {
  const currentAccessToken = localStorage.getItem('accessToken');
  const currentUserId = localStorage.getItem('userId');
  if (!currentAccessToken || !currentUserId) {
    return null;
  }

  return readAccounts().find((item) => item.userId === currentUserId) ?? null;
}

export function clearSessionTokens(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('identitySkRaw');
}

export function clearStoredAccounts(): void {
  localStorage.removeItem(ACCOUNTS_KEY);
}
